import {
	DocumentFormat,
	DocumentFormatValues,
	DocumentTemplate,
} from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Label,
	RadioGroup,
	RadioGroupItem,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { FileCode, FileText } from "lucide-react";
import React, { useEffect, useMemo, useState, useId } from "react";
import { toast } from "sonner";
import { useGenerateConvention } from "../../../../../api/mutations/documents.mutations";
import { useGetDocumentTemplates } from "../../../../../api/queries/documents.queries";
import {
	useGetDuneSignatories,
	useMe,
} from "../../../../../api/queries/users.queries";

interface GenerateConventionModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	societyId: number;
	onSuccess?: (data?: {
		documentUrl: string;
		documentId: string;
		fileId: string;
		message: string;
	}) => void;
}

const GenerateConventionModal: React.FC<GenerateConventionModalProps> = ({
	open,
	onOpenChange,
	societyId,
	onSuccess,
}) => {
	const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
	const [selectedSignatoryId, setSelectedSignatoryId] = useState<string>("");
	const [selectedFormat, setSelectedFormat] = useState<DocumentFormatValues>(
		DocumentFormat.PDF,
	);
	const formatRadioId = useId();

	const { data: allTemplates, isLoading: isLoadingTemplates } =
		useGetDocumentTemplates();

	const { data: duneSignatories, isLoading: isLoadingSignatories } =
		useGetDuneSignatories();

	const { me } = useMe();

	const validTemplates = useMemo(() => {
		return (allTemplates || []).filter(
			(template: DocumentTemplate) =>
				template.templateType === "CONVENTION_APPORTEUR" && template.validated,
		);
	}, [allTemplates]);

	useEffect(() => {
		if (me && duneSignatories && !selectedSignatoryId) {
			const currentUserSignatory = duneSignatories.find(
				(signatory) => signatory.id === me.id,
			);
			if (currentUserSignatory) {
				setSelectedSignatoryId(currentUserSignatory.id.toString());
			}
		}
	}, [me, duneSignatories, selectedSignatoryId]);

	const generateConvention = useGenerateConvention(societyId);

	const handleGenerate = async () => {
		if (!selectedTemplateId) {
			toast.error("Veuillez sélectionner un modèle de convention");
			return;
		}

		if (!selectedSignatoryId) {
			toast.error("Veuillez sélectionner un signataire Dune");
			return;
		}

		generateConvention.mutate(
			{
				templateId: selectedTemplateId,
				signataireDuneId: Number(selectedSignatoryId),
				format: selectedFormat,
			},
			{
				onSuccess: (data) => {
					toast.success("Convention générée avec succès");
					onOpenChange(false);
					if (onSuccess) {
						onSuccess({
							...data,
							documentId: data.documentId.toString(),
						});
					}
				},
				onError: (error: unknown) => {
					const message =
						error instanceof Error
							? error.message
							: "Erreur lors de la génération";
					toast.error(message);
				},
			},
		);
	};

	const formatOptions = [
		{
			value: DocumentFormat.PDF,
			label: "PDF",
			description: "Pour signature électronique",
			icon: FileText,
		},
		{
			value: DocumentFormat.DOCX,
			label: "Word",
			description: "Pour permettre la modification",
			icon: FileCode,
		},
	];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Générer une convention d'apporteur</DialogTitle>
					<DialogDescription>
						Sélectionnez le modèle de convention, le signataire Dune et le
						format de document
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<label htmlFor="template-select" className="text-sm font-medium">
							Modèle de convention
						</label>
						<Select
							value={selectedTemplateId}
							onValueChange={setSelectedTemplateId}
							disabled={isLoadingTemplates || validTemplates.length === 0}
						>
							<SelectTrigger id="template-select">
								<SelectValue
									placeholder={
										isLoadingTemplates
											? "Chargement..."
											: validTemplates.length === 0
												? "Aucun modèle disponible"
												: "Sélectionnez un modèle"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{validTemplates.map((template: DocumentTemplate) => (
									<SelectItem key={template.id} value={template.id}>
										{template.name} (v{template.version})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<label htmlFor="signatory-select" className="text-sm font-medium">
							Signataire Dune
						</label>
						<Select
							value={selectedSignatoryId}
							onValueChange={setSelectedSignatoryId}
							disabled={isLoadingSignatories || !duneSignatories?.length}
						>
							<SelectTrigger id="signatory-select">
								<SelectValue
									placeholder={
										isLoadingSignatories
											? "Chargement..."
											: !duneSignatories?.length
												? "Aucun signataire disponible"
												: "Sélectionnez un signataire"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{duneSignatories?.map((signatory) => (
									<SelectItem
										key={signatory.id}
										value={signatory.id.toString()}
									>
										{signatory.firstname} {signatory.lastname}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<fieldset className="space-y-2">
						<legend className="text-sm font-medium">Format du document</legend>
						<RadioGroup
							value={selectedFormat}
							onValueChange={(value) =>
								setSelectedFormat(value as DocumentFormatValues)
							}
							className="grid grid-cols-2 gap-3"
						>
							{formatOptions.map((option) => {
								const Icon = option.icon;
								return (
									<Label
										key={`${formatRadioId}-${option.value}`}
										htmlFor={`${formatRadioId}-${option.value}`}
										className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
									>
										<RadioGroupItem
											id={`${formatRadioId}-${option.value}`}
											value={option.value}
											className="absolute top-2 right-2"
										/>
										<Icon className="h-8 w-8 mb-1" />
										<span className="font-medium text-center">
											{option.label}
										</span>
										<span className="text-xs text-muted-foreground text-center">
											{option.description}
										</span>
									</Label>
								);
							})}
						</RadioGroup>
					</fieldset>

					{selectedTemplateId && selectedSignatoryId && (
						<div className="rounded-lg bg-blue-50 p-3">
							<p className="text-sm text-blue-800">
								La convention sera générée au format{" "}
								{selectedFormat === DocumentFormat.PDF ? "PDF" : "Word"} et
								ajoutée automatiquement aux documents de la société.
							</p>
						</div>
					)}

					{validTemplates.length === 0 && !isLoadingTemplates && (
						<div className="rounded-lg bg-yellow-50 p-3">
							<p className="text-sm text-yellow-800">
								Aucun modèle de convention validé n'est disponible. Veuillez
								d'abord créer et valider un modèle.
							</p>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleGenerate}
						disabled={
							!selectedTemplateId ||
							!selectedSignatoryId ||
							generateConvention.isPending ||
							validTemplates.length === 0
						}
						isLoading={generateConvention.isPending}
					>
						Générer la convention
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default GenerateConventionModal;
